import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

interface GoogleAnalyticsProps {
  measurementId: string;
}

const GoogleAnalytics: React.FC<GoogleAnalyticsProps> = ({ measurementId }) => {
  const location = useLocation();

  useEffect(() => {
    // Initialize dataLayer exactly as Google specifies
    window.dataLayer = window.dataLayer || [];

    // Define gtag function exactly as Google specifies
    function gtag(...args: any[]) {
      window.dataLayer.push(arguments);
    }

    // Make gtag globally available
    window.gtag = gtag;

    // Check if script already loaded (e.g., from prerendering)
    const existingScript = document.querySelector(`script[src*="gtag/js?id=${measurementId}"]`);

    if (existingScript) {
      // Script exists from prerender - just initialize gtag
      (window.gtag as any)('js', new Date());
      (window.gtag as any)('config', measurementId);
    } else {
      // Load the Google Analytics script dynamically
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      document.head.appendChild(script);

      // Initialize GA after script loads
      script.onload = () => {
        (window.gtag as any)('js', new Date());
        (window.gtag as any)('config', measurementId);
      };
    }
  }, [measurementId]);

  // Track page changes
  useEffect(() => {
    if (typeof window.gtag === 'function') {
      (window.gtag as any)('config', measurementId, {
        page_path: location.pathname,
        page_title: document.title,
      });
    }
  }, [location, measurementId]);

  return null;
};

export default GoogleAnalytics;

// Utility functions for tracking events
export const trackEvent = (action: string, category: string, label?: string, value?: number) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

export const trackPageView = (pagePath: string, pageTitle?: string) => {
  if (typeof window.gtag === 'function') {
    window.gtag('config', process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-6T416ZYYYR', {
      page_path: pagePath,
      page_title: pageTitle || document.title,
    });
  }
};

export const trackCustomEvent = (eventName: string, parameters?: Record<string, any>) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, parameters);
  }
};

// Specific tracking functions for your app
export const trackBillView = (billTitle: string, stateName?: string) => {
  trackEvent('bill_page_view', 'bills', stateName ? `${billTitle} - ${stateName}` : billTitle);
};

export const trackLegislatureEmailAction = (billTitle: string, action: string) => {
  trackEvent('legislature_email_action', 'engagement', `${action} - ${billTitle}`);
};

export const trackDonationClick = (paymentMethod?: string) => {
  trackEvent('donation_click', 'engagement', paymentMethod || 'dialog_opened');
};

export const trackSocialShare = (platform: string, content: string) => {
  trackEvent('social_share', 'social', `${platform} - ${content}`);
};

export const trackDarkModeToggle = (mode: 'light' | 'dark') => {
  trackEvent('dark_mode_toggle', 'ui', mode);
};

export const trackLocationDetect = (stateName: string) => {
  trackEvent('location_detect', 'location', stateName);
};

// NOTE: Not yet wired — the GHL form is embedded via iframe with no native
// submission callback. To fire this, add a window.addEventListener('message', ...)
// listener that detects GoHighLevel's form-submitted postMessage event.
export const trackJoinTeamSubmit = (success: boolean) => {
  trackEvent('join_team_form_submit', 'forms', success ? 'success' : 'error', success ? 1 : 0);
};

export const trackScheduleCall = (location: string) => {
  trackEvent('calendly_schedule_click', 'engagement', location);
};

export const trackLegislatorPackClick = (billTitle: string, stateName?: string) => {
  trackEvent('legislator_pack_view', 'engagement', stateName ? `${billTitle} - ${stateName}` : billTitle);
};

export const trackOfficialBillLinkClick = (billTitle: string, stateName?: string) => {
  trackEvent('official_bill_link_click', 'engagement', stateName ? `${billTitle} - ${stateName}` : billTitle);
};

export const trackShareButtonClick = (billTitle: string, stateName?: string) => {
  trackEvent('share_menu_open', 'engagement', stateName ? `${billTitle} - ${stateName}` : billTitle);
};

export const trackLegislatorMeetingSubmit = (success: boolean) => {
  trackEvent('legislator_meeting_submit', 'forms', success ? 'success' : 'error', success ? 1 : 0);
};

export const trackBlogArticleClick = (articleTitle: string) => {
  trackEvent('blog_article_click', 'blog', articleTitle);
};

/**
 * Track Web Vitals metrics in GA4
 *
 * Sends Core Web Vitals (LCP, CLS, INP) to Google Analytics 4.
 * Uses delta values for proper aggregation.
 *
 * @see https://github.com/GoogleChrome/web-vitals#send-the-results-to-google-analytics
 */
export interface WebVitalsMetric {
  name: 'CLS' | 'FCP' | 'INP' | 'LCP' | 'TTFB';
  value: number;
  delta: number;
  id: string;
  rating?: 'good' | 'needs-improvement' | 'poor';
}

export const trackWebVitals = (metric: WebVitalsMetric) => {
  if (typeof window.gtag !== 'function') {
    return;
  }

  window.gtag('event', metric.name, {
    // Use delta so values can be summed in GA4
    value: Math.round(metric.name === 'CLS' ? metric.delta * 1000 : metric.delta),
    // Custom params for aggregation and debugging
    event_category: 'Web Vitals',
    metric_id: metric.id,
    metric_value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
    metric_rating: metric.rating,
    non_interaction: true,
  });
};
